import { useEffect, useState } from "react";
import "react-day-picker/style.css";
import { DayPicker, type DateRange } from "react-day-picker";
import { uk } from "react-day-picker/locale";
import { Calendar } from "lucide-react";
import {
    Box,
    Button,
    Icon,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Portal,
} from "@chakra-ui/react";

const toInput = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
};

export function DateRangePicker({
                                    start,
                                    end,
                                    placeholder,
                                    onChange,
                                }: {
    start: string;
    end: string;
    placeholder: string;
    onChange: (start: string, end: string) => void;
}) {
    const externalSelected: DateRange | undefined =
        start && end ? { from: new Date(start), to: new Date(end) } : undefined;

    // Local, in-progress selection. DayPicker needs to see the "from-only"
    // click immediately, before the parent commits a full range — otherwise
    // every second click looks like the start of a brand-new range.
    const [range, setRange] = useState<DateRange | undefined>(externalSelected);

    // Keep in sync if the parent resets start/end externally (e.g. switching
    // the period dropdown away from "custom" and back).
    useEffect(() => {
        setRange(externalSelected);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [start, end]);

    const label = start && end ? `${start} – ${end}` : placeholder;

    const todayEnd = new Date();
    todayEnd.setHours(23, 59, 59, 999);

    return (
        <Popover placement="bottom-start" isLazy>
            <PopoverTrigger>
                <Button
                    size="sm"
                    variant="outline"
                    minW="220px"
                    justifyContent="flex-start"
                    leftIcon={<Icon as={Calendar} boxSize={4} />}
                >
                    {label}
                </Button>
            </PopoverTrigger>
            <Portal>
                <PopoverContent w="auto">
                    <PopoverArrow />
                    <PopoverBody>
                        <Box
                            sx={{
                                "--rdp-accent-color": "#FF7C49",
                                "--rdp-accent-background-color": "#FFE7DC",
                            }}
                        >
                            <DayPicker
                                mode="range"
                                numberOfMonths={2}
                                selected={range}
                                disabled={{ after: todayEnd }}
                                endMonth={new Date()}
                                defaultMonth={new Date(new Date().getFullYear(), new Date().getMonth() - 1)}
                                // @ts-expect-error — nested date-fns type mismatch (root date-fns@2 vs react-day-picker's internal date-fns@4); works fine at runtime
                                locale={uk}
                                onSelect={(r) => {
                                    setRange(r);
                                    if (r?.from && r?.to) onChange(toInput(r.from), toInput(r.to));
                                }}
                            />
                        </Box>
                    </PopoverBody>
                </PopoverContent>
            </Portal>
        </Popover>
    );
}